import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Hint} from '@/components/ui/hint'
import {useUpgradeGate} from '@/components/billing/use-upgrade-gate'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {ConfirmarAltaDialog} from '@/components/confirmar-identidad-dialog'
import {avisarSiQuedaEnVerificacion, pideConfirmarIdentidad} from '@/hooks/platform/use-access'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, documentoNormalizado, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Alta mínima sin salir del flujo; usa el mismo `POST /employees`, con su dedup por cédula y
 * su gate de banda. */
export function EmployeeQuickCreate({
  businessId,
  roster,
  onCreated,
  onCancel,
}: {
  businessId: string
  roster: Employee[]
  onCreated: (employee: Employee) => void
  onCancel: () => void
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cedula, setCedula] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('CC')
  const [confirmando, setConfirmando] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const upgradeGate = useUpgradeGate(businessId, 'empleado', (band) =>
    setError(t('planFacturacion.upgradeModal.retryNote', {band}))
  )

  const documentoTecleado = documentoNormalizado(tipoDocumento, cedula)
  const yaExiste = useMemo(
    () =>
      documentoTecleado
        ? roster.find((e) => documentoNormalizado(e.tipo_documento, e.cedula) === documentoTecleado)
        : undefined,
    [roster, documentoTecleado]
  )
  const cedulaMal = cedula.trim() ? validateCedula(cedula, tipoDocumento) : null
  const invalido =
    validateRequired(name) ??
    validateRequired(lastName) ??
    validateCedula(cedula, tipoDocumento) ??
    (yaExiste ? '' : null)

  async function crear(confirmar_identidad: boolean) {
    setPending(true)
    setError(null)
    setConfirmando(false)
    try {
      const created = await api.post<Employee>(`/businesses/${businessId}/employees`, {
        cedula: documentoTecleado,
        tipo_documento: tipoDocumento,
        name: name.trim(),
        last_name: lastName.trim(),
        role: 'user',
        confirmar_identidad,
      })
      void queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      avisarSiQuedaEnVerificacion(created.data, t)
      onCreated(created.data)
    } catch (err) {
      if (pideConfirmarIdentidad(err)) setConfirmando(true)
      else if (!upgradeGate.catchGate(err, {proposed_active_delta: 1})) setError(translateApiError(err))
    } finally {
      setPending(false)
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (invalido !== null) return
    void crear(false)
  }

  return (
    <form
      noValidate
      onSubmit={submit}
      className="space-y-3 rounded-lg border border-border bg-secondary/40 p-3"
      data-testid="empleado-alta-rapida">
      <Hint>{t('incidentes.reportar.altaRapida.hint')}</Hint>
      <div className="space-y-1.5">
        <Label htmlFor="alta_rapida_name">{t('employees.fields.name')}</Label>
        <Input
          id="alta_rapida_name"
          value={name}
          maxLength={tope('NOM')}
          data-testid="empleado-alta-rapida-nombre"
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="alta_rapida_last_name">{t('employees.fields.lastName')}</Label>
        <Input
          id="alta_rapida_last_name"
          value={lastName}
          maxLength={tope('NOM')}
          data-testid="empleado-alta-rapida-apellido"
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="alta_rapida_cedula">{t('documento.label')}</Label>
        <div className="flex gap-2">
          <TipoDocumentoSelect
            value={tipoDocumento}
            onChange={setTipoDocumento}
            testId="empleado-alta-rapida-tipo-documento"
          />
          <Input
            id="alta_rapida_cedula"
            value={cedula}
            maxLength={DOCUMENTO_MAX[tipoDocumento]}
            className="flex-1 font-mono"
            data-testid="empleado-alta-rapida-cedula"
            onChange={(e) => setCedula(e.target.value)}
          />
        </div>
        <Hint>{t(tipoDocumento === 'PA' ? 'documento.hintPA' : 'documento.hintCC')}</Hint>
        {cedulaMal && (
          <p className="text-xs text-destructive" data-testid="empleado-alta-rapida-cedula-error">
            {t(cedulaMal)}
          </p>
        )}
      </div>
      {yaExiste && (
        <div className="tone-amber space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="empleado-alta-rapida-duplicado">
          <p>{t('plan.picker.nearBody', {name: `${yaExiste.name} ${yaExiste.last_name}`, cedula: yaExiste.cedula})}</p>
          <Button type="button" size="sm" data-testid="empleado-alta-rapida-usar" onClick={() => onCreated(yaExiste)}>
            {t('plan.picker.useExisting')}
          </Button>
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="empleado-alta-rapida-error">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={invalido !== null || pending}
          data-testid="empleado-alta-rapida-guardar">
          {t('incidentes.reportar.altaRapida.guardar')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} data-testid="empleado-alta-rapida-cancelar">
          {t('common.cancel')}
        </Button>
      </div>
      {upgradeGate.modal}
      <ConfirmarAltaDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        nombre={`${name.trim()} ${lastName.trim()}`}
        cedula={documentoTecleado}
        onConfirm={() => void crear(true)}
        pending={pending}
      />
    </form>
  )
}
